import { Box, TextFieldProps } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { ChangeEvent, ReactNode, Ref, forwardRef, useRef } from 'react';
import { ClearButton } from './ClearButton';
import { SelectIcon } from './SelectIcon';
import { WbCheckboxMenuItem } from './WbCheckboxMenuItem';
import { WbChip } from './WbChip';
import { WbTextField, AccessibilityProps } from './WbTextField';

type MultiSelectConditionalProps =
  | {
      options: string[];
      getOptionLabel?: (value: string) => string;
      getDisabled?: (value: string) => boolean;
      children?: never;
    }
  | {
      options?: never;
      getOptionLabel?: never;
      getDisabled?: never;
      children: ReactNode;
    };

type MultiSelectSharedProps = {
  value: string[];
  onChange: (event: ChangeEvent<{ value: string[] }>) => void;
};

type Props = Omit<TextFieldProps, 'select' | 'onChange' | 'value'> &
  AccessibilityProps &
  MultiSelectSharedProps &
  MultiSelectConditionalProps & {
    clearable?: boolean;
    limitSelectedDisplay?: number;
    expandChipsVertically?: boolean;
    id?: string;
    ariaLabel?: string;
    label?: string | React.ReactNode;
  };

export const WbMultiSelect = forwardRef(
  (
    {
      clearable,
      limitSelectedDisplay,
      expandChipsVertically = true,
      getOptionLabel,
      getDisabled,
      id,
      label,
      ariaLabel,
      ...props
    }: Props,
    ref?: Ref<any>,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const accessibilityProps =
      id && label
        ? { id, label }
        : { id, ariaLabel: ariaLabel ?? 'select field' };

    return (
      <WbTextField
        {...props}
        {...accessibilityProps}
        ref={ref}
        value={props.value || []}
        onChange={e => {
          const event = e as unknown as ChangeEvent<{ value: string[] }>;
          props.onChange(event);
        }}
        select
        SelectProps={{
          inputRef: inputRef,
          IconComponent: SelectIcon,
          multiple: true,
          renderValue: selected => (
            <Box
              style={{
                display: 'flex',
                flexWrap: expandChipsVertically ? 'wrap' : undefined,
                ...(props.disabled
                  ? { opacity: 0.5, pointerEvents: 'none' }
                  : {}),
              }}
            >
              {(limitSelectedDisplay
                ? (selected as string[])?.slice(0, limitSelectedDisplay)
                : (selected as string[])
              )?.map(value => {
                return (
                  <WbChip
                    key={value}
                    label={getOptionLabel?.(value) || value}
                    onDelete={e => {
                      const event = e as unknown as ChangeEvent<{
                        value: string[];
                      }>;
                      const newValue = props.value.filter(v => v !== value);
                      event.target.value = newValue;
                      props.onChange(event);
                      inputRef?.current?.focus();
                    }}
                    deleteIcon={
                      <ClearIcon
                        onMouseDown={event => event.stopPropagation()}
                      />
                    }
                  />
                );
              })}
              {limitSelectedDisplay &&
                (selected as string[]).length > limitSelectedDisplay && (
                  <WbChip
                    key={`wbchip-${
                      (selected as string[]).length - limitSelectedDisplay
                    }`}
                    label={`+${
                      (selected as string[]).length - limitSelectedDisplay
                    }`}
                  />
                )}
            </Box>
          ),
          ...props.SelectProps,
        }}
        InputProps={{
          endAdornment: clearable && !props.disabled && (
            <ClearButton
              style={{
                marginRight: '25px',
              }}
              onClear={e => {
                const event = e as unknown as ChangeEvent<{
                  value: string[];
                }>;
                event.target.value = [];
                if (props.onChange) props.onChange(event);
              }}
            />
          ),
          ...props.InputProps,
        }}
      >
        {props?.options?.map((option, i) => (
          <WbCheckboxMenuItem
            key={i}
            value={option}
            disabled={getDisabled ? getDisabled(option) : false}
            checked={props.value?.includes(option)}
            label={getOptionLabel ? getOptionLabel(option) : option}
          />
        ))}
        {props?.children}
      </WbTextField>
    );
  },
);
